// frontend/src/components/common/PortalLinkTabs.js
//
// Small pill-style link strip pairing two (or more) related portal pages that
// live on separate routes but should read as one surface (e.g. Transactions
// | Voids). Purely a navigation control — it does not own any page state, and
// renders nothing if fewer than 2 tabs survive gating (nothing to switch to).
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const LockIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

/**
 * Builds the gated tab config for the Transactions | Voids pair. Same gate
 * keys ('transactions', 'voids') the old sidebar nav items used — pass in
 * PortalLayout's NAV_FEATURE_MAP / NAV_PERMISSION_MAP so both pages stay in
 * sync with the sidebar's gate semantics: a permission failure drops the tab
 * entirely, a feature failure keeps it visible with a padlock (navigating to
 * it still renders FeatureLocked, same as any other feature-gated page).
 *
 * @param {object} args
 * @param {(key:string)=>string} args.t
 * @param {(permKey:string)=>boolean} args.hasPermission
 * @param {(featureKey:string)=>boolean} [args.hasFeature]
 * @param {object} args.navFeatureMap      PortalLayout's NAV_FEATURE_MAP
 * @param {object} args.navPermissionMap   PortalLayout's NAV_PERMISSION_MAP
 */
export function buildTransactionsVoidsTabs({ t, hasPermission, hasFeature, navFeatureMap, navPermissionMap }) {
    return [
        { key: 'transactions', navId: 'transactions', path: '/portal/transactions', label: t('Transactions') },
        { key: 'voids', navId: 'voids', path: '/portal/voids', label: t('Voids') },
        // Refunds share the Voids permission and nav entry: money and orders
        // taken back are one audit surface. Refunds are NOT written as Void
        // rows — a Void asserts the record was deleted, and a refunded sale
        // is still standing.
        { key: 'refunds', navId: 'voids', path: '/portal/refunds', label: t('Refunds') },
    ]
        .filter(tab => hasPermission(navPermissionMap[tab.navId]))
        .map(tab => ({
            ...tab,
            locked: !!(navFeatureMap[tab.navId] && hasFeature && !hasFeature(navFeatureMap[tab.navId])),
        }));
}

/**
 * @param {object} props
 * @param {Array<{key:string, label:string, path:string, locked?:boolean}>} props.tabs
 * @param {string} props.active   key of the tab matching the current page
 */
export default function PortalLinkTabs({ tabs, active }) {
    const navigate = useNavigate();
    if (!tabs || tabs.length < 2) return null;

    return (
        <div style={{
            display: 'inline-flex', background: C.bg, borderRadius: 8,
            border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 16, fontFamily: FONT,
        }}>
            {tabs.map(tab => {
                const isActive = active === tab.key;
                return (
                    <button
                        key={tab.key}
                        onClick={() => { if (!isActive) navigate(tab.path); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontFamily: FONT, fontSize: 12.5, fontWeight: 700, padding: '8px 16px',
                            border: 'none', cursor: isActive ? 'default' : 'pointer',
                            background: isActive ? C.green : 'transparent',
                            color: isActive ? C.white : C.muted,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tab.label}
                        {tab.locked && <LockIcon />}
                    </button>
                );
            })}
        </div>
    );
}
